<template>
  <a-modal
    title="Add Several Shifts"
    width="1000px"
    :visible="visible"
    data-testid="shifts-add-multiple-modal"
    @cancel="cancel"
  >
    <shift-overlap-modal
      :visible.sync="visibleOverlapModal"
      :overlapping-shifts="overlappingShifts"
      :loading="loadingCheck"
      data-testid="shifts-add-multiple-overlap-modal"
      @reload="checkConflicts"
    />

    <a-modal
      class="duplicate-shift-modal"
      v-model="visibleDuplicateShiftModal"
      title="Duplicate Shift"
      data-testid="shifts-add-multiple-duplicate-modal"
      @cancel="cancelDuplicate"
    >
      <div v-if="shiftToDuplicate" class="flex flex-column gap-3">
        <div>
          You can duplicate a created shift over a span of dates or a single date.
        </div>
        <div class="flex flex-column gap-1">
          <div>Duration: {{ getSignedDuration(shiftToDuplicate) }}</div>
          <div v-if="shiftToDuplicate.lunch_start">
            1st Meal Break: {{ getFirstLunchDuration(shiftToDuplicate) ?? "-" }}
          </div>
          <div v-if="shiftToDuplicate.second_lunch_start">
            2nd Meal Break:
            {{ getSecondLunchDuration(shiftToDuplicate) ?? "-" }}
          </div>
        </div>
        <a-radio-group v-model="duplicateType" button-style="solid">
          <a-radio-button value="span"> Span </a-radio-button>
          <a-radio-button value="single"> Single </a-radio-button>
        </a-radio-group>

        <div v-if="spanDuplicate" class="flex flex-row gap-2">
          <a-range-picker
            format="MM/DD/YYYY"
            v-model="dateProxy"
            :valueFormat="visualDateFormat"
            :ranges="dateRange"
          />
        </div>
        <div v-else-if="singleDuplicate">
          <a-date-picker
            placeholder="Date"
            v-model="singleDuplicateDate"
            :disabled-date="disabledDate"
            :format="visualDateFormat"
          />
        </div>
      </div>

      <template slot="footer">
        <div class="flex justify-content-between">
          <a-button type="danger" @click="cancelDuplicate"> Cancel </a-button>
          <a-button type="primary" @click="confirmDuplicate"> Duplicate Shift </a-button>
        </div>
      </template>
    </a-modal>

    <shift-add
      ref="shiftForm"
      :visible.sync="visibleAssociateShiftModal"
      :multiply="true"
      :initial-values="initialShiftValues"
      @edit="handleAddAssociateShift"
      @fetchUser="afterFetchUser"
    />
    <div class="flex flex-column gap-4">
      <div class="flex gap-2">
        <search-associates class="filters-select" :value.sync="associate_number" />
        <a-button
          type="primary"
          :disabled="disableAddNewShift"
          @click="openAddShiftModal"
        >
          Add New Shift
        </a-button>
      </div>
      <template v-if="associate_number">
        <a-table
          class="multiple-shifts-table"
          size="small"
          :columns="shiftsColumns"
          :data-source="shifts"
          :scroll="{ y: 380 }"
          :locale="{ emptyText: 'Please Add Shifts' }"
        >
          <span slot="shift-date" slot-scope="text, record">
            {{ formatDateRange(record.work_start, record.work_end) }}
          </span>
          <span slot="duration" slot-scope="text, record">
            {{ getSignedDuration(record) }}
          </span>
          <span slot="first-lunch" slot-scope="text, record">
            {{ getFirstLunchDuration(record) ?? "-" }}
          </span>
          <span slot="second-lunch" slot-scope="text, record">
            {{ getSecondLunchDuration(record) ?? "-" }}
          </span>
          <span slot="actions" slot-scope="text, record" class="flex gap-2">
            <a-tooltip>
              <template slot="title"> Copy Shift </template>
              <a-button icon="copy" @click="initDuplicate(record)" />
            </a-tooltip>
            <a-tooltip>
              <template slot="title"> Delete Shift </template>
              <a-button icon="delete" @click="deleteShift(record)" />
            </a-tooltip>
          </span>
        </a-table>

        <div v-if="shifts.length" class="flex flex-column gap-4">
          <div class="flex flex-column gap-2">
            <h4 class="m-0">How would you like to Sign this Shift?</h4>
            <a-radio-group v-model="signatureType">
              <a-radio value="admin">Sign with <b>MY</b> signature</a-radio>
              <a-radio v-if="canSendToAssociateForSignature" value="associate"
                ><b>SEND TO ASSOCIATE</b> for signature</a-radio
              >
            </a-radio-group>
          </div>

          <div v-if="signatureType === 'admin'" class="flex flex-column gap-2">
            <label for="multiple-shifts-signing-explanation">Explanation (required)</label>
            <a-textarea
              id="multiple-shifts-signing-explanation"
              v-model="explanation"
              :max-length="2048"
              :auto-size="{ minRows: 3, maxRows: 6 }"
              aria-required="true"
              data-testid="shifts-add-multiple-explanation"
            />
          </div>

          <signature
            v-if="signatureType === 'admin'"
            checkbox-label="I confirm that I issued the associate a written warning, and they have viewed it"
            ref="signatureRef"
            @checked="setSignatureCheckboxValue"
            @save-signature="setSignatureId"
          /></div
      ></template>
    </div>
    <template slot="footer">
      <div class="flex justify-content-between">
        <a-button @click="cancel"> Cancel </a-button>
        <a-button
          type="primary"
          :loading="loading"
          :disabled="disableAddShifts"
          @click="create"
        >
          Add Shifts
        </a-button>
      </div>
    </template>
  </a-modal>
</template>

<script>
import {
  Modal,
  Button,
  Input,
  Table,
  Radio,
  DatePicker,
  notification,
  Tooltip,
} from "ant-design-vue";
import shiftsHelper from "@/helpers/shifts";
import moment from "moment-timezone";
import signature from "@/components/signature.vue";
import ShiftForm from "../form/shift-form.vue";
import SearchAssociates from "@/components/search-associates.vue";
import ShiftOverlapModal from "./shift-overlap-modal.vue";
import { deepCopy } from "@/utils/deep-copy";

export default {
  components: {
    signature,
    "shift-add": ShiftForm,
    "a-table": Table,
    "a-modal": Modal,
    "a-button": Button,
    "a-textarea": Input.TextArea,
    "a-tooltip": Tooltip,
    "a-radio-button": Radio.Button,
    "a-radio-group": Radio.Group,
    "a-date-picker": DatePicker,
    "a-range-picker": DatePicker.RangePicker,
    "a-radio": Radio,
    "search-associates": SearchAssociates,
    "shift-overlap-modal": ShiftOverlapModal,
  },
  mixins: [shiftsHelper],
  data() {
    return {
      shifts: [],

      associate_number: undefined,
      user: undefined,
      isAssociateDisabled: false,

      initialShiftValues: this.initialValues,

      shiftsColumns: [
        {
          title: "Date",
          dataIndex: "work_start",
          key: "date",
          scopedSlots: { customRender: "shift-date" },
          sorter: (a, b) => moment(a.work_start).diff(moment(b.work_start)),
        },
        {
          title: "Duration",
          key: "duration",
          scopedSlots: { customRender: "duration" },
          sorter: (a, b) => {
            const durationA = moment(a.work_end).diff(moment(a.work_start));
            const durationB = moment(b.work_end).diff(moment(b.work_start));
            return durationA - durationB;
          },
        },
        {
          title: "1st Meal Break",
          key: "firstLunch",
          scopedSlots: { customRender: "first-lunch" },
          sorter: (a, b) => {
            if (!a.lunch_start && !b.lunch_start) return 0;
            if (!a.lunch_start) return 1;
            if (!b.lunch_start) return -1;
            return moment(a.lunch_start).diff(moment(b.lunch_start));
          },
        },
        {
          title: "2nd Meal Break",
          key: "secondLunch",
          scopedSlots: { customRender: "second-lunch" },
          sorter: (a, b) => {
            if (!a.second_lunch_start && !b.second_lunch_start) return 0;
            if (!a.second_lunch_start) return 1;
            if (!b.second_lunch_start) return -1;
            return moment(a.second_lunch_start).diff(moment(b.second_lunch_start));
          },
        },
        {
          title: "Actions",
          key: "actions",
          scopedSlots: { customRender: "actions" },
        },
      ],

      visibleAssociateShiftModal: false,
      visibleOverlapModal: false,
      visibleDuplicateShiftModal: false,
      shiftToDuplicate: undefined,
      duplicateType: "span",

      spanDuplicateDateFrom: undefined,
      spanDuplicateDateTo: undefined,
      singleDuplicateDate: undefined,

      signatureCheckbox: false,
      signatureId: undefined,
      signatureType: "associate",
      explanation: "",

      loading: false,
      loadingCheck: false,

      overlappingShifts: [],
    };
  },
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    initialValues: {
      type: Object,
    },
  },
  computed: {
    roles() {
      return this.$store.state.applicationState.projectRoles;
    },
    visualDateFormat() {
      return this.$store.state.applicationState.visualDateFormat;
    },
    projectTimeZone() {
      return this.$store.state.applicationState.timeZone;
    },

    disableAddShifts() {
      return (
        this.shifts.length === 0 ||
        (this.signatureType === "admin" && (!this.signatureCheckbox || !!this.signingExplanationError))
      );
    },
    signingExplanation() {
      return this.signatureType === "admin" ? this.explanation.trim() : undefined;
    },
    signingExplanationError() {
      if (this.signatureType !== "admin") return null;
      if (!this.signingExplanation) return "Please explain why you changed these shifts and signed them yourself.";
      if (this.signingExplanation.length > 2048) return "Explanation must be 2048 characters or fewer.";
      return null;
    },
    disableAutoSignCheckbox() {
      return this.shifts.length === 0;
    },
    disableAddNewShift() {
      return !this.associate_number;
    },
    canSendToAssociateForSignature() {
      return !this.isAssociateDisabled;
    },

    spanDuplicate() {
      return this.duplicateType === "span";
    },
    singleDuplicate() {
      return this.duplicateType === "single";
    },

    dateRange() {
      return {
        Today: [moment(), moment()],
        "This Month": [moment().startOf("month"), moment().endOf("month")],
      };
    },

    dateProxy: {
      get() {
        return [this.spanDuplicateDateFrom, this.spanDuplicateDateTo];
      },
      set(value) {
        this.spanDuplicateDateFrom = value[0];
        this.spanDuplicateDateTo = value[1];
      },
    },
  },
  watch: {
    visible: {
      handler(newVal) {
        if (newVal) {
          this.explanation = "";
          this.signatureId = undefined;
          this.signatureCheckbox = false;
          this.signatureType = "associate";
          this.initialShiftValues = this.initialValues;

          if (this.initialValues.associateNumber) {
            this.associate_number = this.initialValues.associateNumber;
          }
        }
      },
      immediate: true,
    },
    shiftToDuplicate() {
      if (!this.shiftToDuplicate) {
        this.visibleDuplicateShiftModal = false;
        return;
      }

      this.visibleDuplicateShiftModal = true;
    },

    associate_number: {
      handler(newVal) {
        this.explanation = "";
        this.signatureId = undefined;
        this.signatureCheckbox = false;
        this.initialShiftValues = {
          ...this.initialShiftValues,
          associateNumber: newVal,
        };

        this.shifts = [];
      },
      deep: true,
      immediate: true,
    },
    signatureType: {
      handler() {
        this.explanation = "";
        this.signatureId = undefined;
        this.signatureCheckbox = false;
      },
    },
  },
  methods: {
    cancel() {
      this.explanation = "";
      this.signatureId = undefined;
      this.shifts = [];
      this.associate_number = undefined;
      this.signatureType = "associate";
      this.signatureCheckbox = false;
      this.user = undefined;
      this.isAssociateDisabled = false;
      this.overlappingShifts = [];
      this.$emit("close", false);
    },
    setSignatureId(signatureId) {
      this.signatureId = signatureId;
      this.creatingShifts();
    },

    afterFetchUser(payload) {
      this.user = deepCopy(payload?.user ?? payload);
      this.isAssociateDisabled = !!payload?.isAssociateDisabled;
    },

    successNotification() {
      notification.success({
        message: "Success",
        description: "Associate shifts saved successfully",
      });
    },
    errorNotification(text) {
      notification.warning({
        message: "Error",
        description:
          text ?? "An error occurred on several shifts creation. Please try again later",
      });
    },

    async create() {
      if (this.signingExplanationError) {
        this.errorNotification(this.signingExplanationError);
        return;
      }
      if (this.signatureType === "admin") {
        await this.$refs.signatureRef.saveSignature();
      } else {
        this.creatingShifts();
      }
    },

    openOverlapShifts(shifts) {
      this.visibleOverlapModal = true;
      this.overlappingShifts = deepCopy(shifts);
    },

    async checkConflicts() {
      this.loadingCheck = true;

      try {
        const promises = this.shifts.map((record) =>
          this.$store.dispatch("shifts/checkShiftConflict", {
            associateNumber: this.associate_number,
            start: record.work_start,
            end: record.work_end,
          })
        );

        const responses = await Promise.all(promises);

        this.overlappingShifts = responses.flatMap((response) =>
          (response.result || []).flatMap((resultItem) => resultItem.shifts || [])
        );
      } catch (error) {
        this.errorNotification(typeof error === "string" ? error : error.message);
      } finally {
        this.loadingCheck = false;
      }
    },

    async creatingShifts() {
      if (this.signingExplanationError) {
        this.errorNotification(this.signingExplanationError);
        return;
      }
      this.loading = true;

      const wrappedShifts = this.shifts.map((shift) => ({
        shift: {
          ...shift,
          explanation: this.signingExplanation,
          modified_by_admin: true,
          signature: this.signatureType === "admin" ? { id: this.signatureId } : undefined,
        },
        associate: {
          associate_number: this.associate_number,
        },
      }));

      try {
        await this.checkConflicts();

        if (this.overlappingShifts.length) {
          this.visibleOverlapModal = true;
          return;
        }

        const data = await this.$store.dispatch("shifts/addSeveralShifts", {
          shifts: wrappedShifts,
          autoSign: false,
        });

        if (data.error_code && data.error_code !== "0") {
          this.errorNotification(data.msg);
        } else {
          this.successNotification();
          this.$emit("close", true);
        }
      } catch (error) {
        this.errorNotification(typeof error === "string" ? error : error.message);
      } finally {
        this.loading = false;
      }
    },

    getSignedDuration(record) {
      const timeRange = this.formatTimeRange(record.work_start, record.work_end);
      return (
        timeRange.start + " - " + timeRange.end + " (" + this.getSignedHours(record) + ")"
      );
    },

    getSignedHours(record) {
      return this.formatWorkHours(
        moment(record.work_start),
        moment(record.work_end),
        record.lunch_start ? moment(record.lunch_start) : null,
        record.lunch_end ? moment(record.lunch_end) : null,
        record.second_lunch_start ? moment(record.second_lunch_start) : null,
        record.second_lunch_end ? moment(record.second_lunch_end) : null
      );
    },

    getFirstLunchDuration(record) {
      if (!record.lunch_start || !record.lunch_end) {
        return null;
      }

      const timeRange = this.formatTimeRange(record.lunch_start, record.lunch_end);
      return (
        timeRange.start +
        " - " +
        timeRange.end +
        " (" +
        this.minutesToHoursAndMinutes(
          this.countDuration(
            moment(record.lunch_start),
            moment(record.lunch_end),
            "minutes"
          )
        ) +
        ")"
      );
    },

    getSecondLunchDuration(record) {
      if (!record.second_lunch_start || !record.second_lunch_end) {
        return null;
      }

      const timeRange = this.formatTimeRange(
        record.second_lunch_start,
        record.second_lunch_end
      );
      return (
        timeRange.start +
        " - " +
        timeRange.end +
        " (" +
        this.minutesToHoursAndMinutes(
          this.countDuration(
            moment(record.second_lunch_start),
            moment(record.second_lunch_end),
            "minutes"
          )
        ) +
        ")"
      );
    },

    handleAddAssociateShift(shiftData) {
      this.shifts.push(shiftData);
    },

    openAddShiftModal() {
      this.visibleAssociateShiftModal = true;
    },

    deleteShift(record) {
      this.shifts = this.shifts.filter((shift) => shift.work_start !== record.work_start);
    },
    initDuplicate(record) {
      this.shiftToDuplicate = record;
    },

    confirmDuplicate() {
      if (!this.shiftToDuplicate) return;

      const duplicateShift = (baseShift, daysOffset) => {
        const newShift = { ...baseShift };
        newShift.work_start = moment(baseShift.work_start)
          .add(daysOffset, "days")
          .toISOString();
        newShift.work_end = moment(baseShift.work_end)
          .add(daysOffset, "days")
          .toISOString();
        if (baseShift.lunch_start) {
          newShift.lunch_start = moment(baseShift.lunch_start)
            .add(daysOffset, "days")
            .toISOString();
        }
        if (baseShift.lunch_end) {
          newShift.lunch_end = moment(baseShift.lunch_end)
            .add(daysOffset, "days")
            .toISOString();
        }
        if (baseShift.second_lunch_start) {
          newShift.second_lunch_start = moment(baseShift.second_lunch_start)
            .add(daysOffset, "days")
            .toISOString();
        }
        if (baseShift.second_lunch_end) {
          newShift.second_lunch_end = moment(baseShift.second_lunch_end)
            .add(daysOffset, "days")
            .toISOString();
        }
        return newShift;
      };

      const shiftExistsOnDate = (date) => {
        return this.shifts.some((shift) => moment(shift.work_start).isSame(date, "day"));
      };

      if (this.duplicateType === "single" && this.singleDuplicateDate) {
        const singleDate = moment(this.singleDuplicateDate).startOf("day");
        if (!shiftExistsOnDate(singleDate)) {
          const daysOffset = singleDate.diff(
            moment(this.shiftToDuplicate.work_start).startOf("day"),
            "days"
          );
          this.shifts.push(duplicateShift(this.shiftToDuplicate, daysOffset));
        }
      } else if (
        this.duplicateType === "span" &&
        this.spanDuplicateDateFrom &&
        this.spanDuplicateDateTo
      ) {
        let currentDate = moment(this.spanDuplicateDateFrom).startOf("day");
        const endDate = moment(this.spanDuplicateDateTo).startOf("day");
        while (currentDate.isSameOrBefore(endDate, "day")) {
          if (!shiftExistsOnDate(currentDate)) {
            const daysOffset = currentDate.diff(
              moment(this.shiftToDuplicate.work_start).startOf("day"),
              "days"
            );
            this.shifts.push(duplicateShift(this.shiftToDuplicate, daysOffset));
          }
          currentDate = currentDate.add(1, "days");
        }
      }

      this.shifts.sort((a, b) => moment(a.work_start).diff(moment(b.work_start)));

      this.shiftToDuplicate = undefined;
    },

    cancelDuplicate() {
      this.shiftToDuplicate = undefined;
    },

    disabledDate(current) {
      if (!current) {
        return false;
      }

      const shiftDates = this.shifts.map((shift) =>
        moment.tz(shift.work_start, this.projectTimeZone).format("MM/DD/YYYY")
      );
      return shiftDates.includes(current.format("MM/DD/YYYY"));
    },

    setSignatureCheckboxValue(e) {
      this.signatureCheckbox = e;
    },
  },
};
</script>

<style scoped>
.content-view-block {
  max-width: unset;
}

.filters-select {
  width: 250px;
}

.filters-select .ant-select-dropdown {
  width: 200px;
}

.filters-select .ant-select-dropdown-menu-item {
  white-space: normal;
  word-wrap: break-word;
}
</style>
